// Cloudflare Stream Configuration and Utilities

export const CLOUDFLARE_CONFIG = {
  accountId: process.env.REACT_APP_CLOUDFLARE_ACCOUNT_ID,
  subdomain: process.env.REACT_APP_CLOUDFLARE_STREAM_SUBDOMAIN,
};

/**
 * Generate Cloudflare Stream video URL
 * @param {string} videoId - The video ID from Cloudflare Stream
 * @param {object} options - Optional parameters
 * @param {number} options.width - Video width
 * @param {number} options.height - Video height
 * @param {boolean} options.autoplay - Autoplay video
 * @param {boolean} options.loop - Loop video
 * @param {boolean} options.muted - Mute video
 * @returns {string} - Stream video URL
 */
export const getStreamVideoUrl = (videoId, options = {}) => {
  const { subdomain } = CLOUDFLARE_CONFIG;
  let url = `https://${subdomain}/${videoId}/iframe`;
  
  const params = new URLSearchParams();
  
  if (options.autoplay) params.append('autoplay', 'true');
  if (options.loop) params.append('loop', 'true');
  if (options.muted) params.append('muted', 'true');
  if (options.width) params.append('width', options.width.toString());
  if (options.height) params.append('height', options.height.toString());
  
  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
};

/**
 * Generate Cloudflare Stream thumbnail URL
 * @param {string} videoId - The video ID from Cloudflare Stream
 * @param {object} options - Optional parameters
 * @param {number} options.width - Thumbnail width
 * @param {number} options.height - Thumbnail height
 * @param {string} options.fit - Fit mode (scale-down, contain, cover, crop, pad)
 * @param {number} options.time - Time in seconds for thumbnail
 * @returns {string} - Thumbnail URL
 */
export const getStreamThumbnailUrl = (videoId, options = {}) => {
  const { subdomain } = CLOUDFLARE_CONFIG;
  let url = `https://${subdomain}/${videoId}/thumbnails/thumbnail.jpg`;
  
  const params = new URLSearchParams();
  
  if (options.width) params.append('width', options.width.toString());
  if (options.height) params.append('height', options.height.toString());
  if (options.fit) params.append('fit', options.fit);
  if (options.time) params.append('time', options.time.toString() + 's');
  
  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
};

/**
 * Generate Cloudflare Images URL (for uploaded images)
 * Note: Cloudflare Images uses a different service, but can be integrated similarly
 * @param {string} imageId - The image ID from Cloudflare Images
 * @param {string} variant - Image variant (e.g., 'public', 'thumbnail', etc.)
 * @returns {string} - Image URL
 */
export const getCloudflareImageUrl = (imageId, variant = 'public') => {
  // Use the correct account hash for Cloudflare Images
  const accountHash = 'l7Ltvo1CrQVhD5XaoTJtJQ';
  return `https://imagedelivery.net/${accountHash}/${imageId}/${variant}`;
};

/**
 * Check if Cloudflare Stream is properly configured
 * @returns {boolean} - Configuration status
 */
export const isStreamConfigured = () => {
  return !!(CLOUDFLARE_CONFIG.accountId && CLOUDFLARE_CONFIG.subdomain);
};

// Video quality presets
export const VIDEO_PRESETS = {
  thumbnail: { width: 320, height: 180 },
  small: { width: 640, height: 360 },
  medium: { width: 1280, height: 720 },
  large: { width: 1920, height: 1080 },
};

// Common video options
export const VIDEO_OPTIONS = {
  autoplay: false,
  loop: false,
  muted: true, // Usually required for autoplay
  controls: true,
};